// Admin ProfilePage — account details (read-only, synced from Google) plus
// an editable phone number for urgent system notifications. No admin_profiles
// table — this reads/writes the users row directly via /api/profile/admin.
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { patchJson } from "../../hooks/useApi.js";
import { phoneSchema } from "@dm-lms/shared/validations";
import { phoneError } from "../../lib/phoneError.js";
import { useProfile } from "../../contexts/ProfileContext.js";
import StatCard from "../../components/shared/StatCard.js";
import Avatar from "../../components/shared/Avatar.js";
import { IconLock } from "../../components/shared/icons.js";

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function ProfilePage() {
  const { profile, isLoading, error, refetch } = useProfile<"admin">();

  const seeded = useRef(false);
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile || seeded.current) return;
    seeded.current = true;
    setPhone(profile.phone ?? "");
  }, [profile]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setPhoneTouched(true);
    if (phoneError(phoneSchema, phone) !== null) return;
    setSaveError(null);
    setSaving(true);

    // A blank phone is omitted: the server keeps the saved number.
    const res = await patchJson("/api/profile/admin", phone.trim() !== "" ? { phone: phone.trim() } : {});

    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error);
      return;
    }
    setSaved(true);
    refetch();
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Profile</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading profile…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && profile && (
        <div className="max-w-2xl">
          <form onSubmit={saveProfile} className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-5 flex items-center gap-4">
              <Avatar name={profile.name} size="md" />
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-dm-navy">{profile.name}</p>
                  <IconLock width={14} height={14} className="text-dm-slate" />
                </div>
                <p className="text-xs text-dm-slate">Synced from Google — contact support to change.</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-dm-navy">{profile.email}</p>
                <IconLock width={14} height={14} className="text-dm-slate" />
              </div>
              <p className="text-xs text-dm-slate">Synced from Google — contact support to change.</p>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3">
              <StatCard label="Admin code" value={profile.code} />
              <StatCard
                label="Member since"
                value={new Date(profile.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              />
            </div>

            <hr className="mb-6 border-gray-200" />

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-dm-navy">
                Phone number
              </label>
              <input
                id="phone"
                type="tel"
                placeholder="+254700000000"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                onBlur={() => setPhoneTouched(true)}
                aria-invalid={phoneTouched && phoneError(phoneSchema, phone) !== null}
                aria-describedby={phoneTouched && phoneError(phoneSchema, phone) !== null ? "phone-error" : undefined}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-dm-slate">Used for urgent system notifications.</p>
              {phoneTouched && phoneError(phoneSchema, phone) !== null && (
                <p id="phone-error" role="alert" className="mt-1 text-xs text-red-600">
                  {phoneError(phoneSchema, phone)}
                </p>
              )}
            </div>

            {saveError && <p className="mt-3 text-sm text-red-600">{saveError}</p>}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              {saved && <span className="text-sm text-green-600">Saved</span>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
